import * as d3 from 'd3';

import BoundingBox from './BoundingBox';

export default class VirtualDomElement {
  static idCounter = 0;

  children: any[];
  attributes: object;
  animation = null;
  private _isAnimated = false;
  private _fadingIn = false;
  private _fadingOut = false;
  private fadeInAnimation;
  private fadeOutAnimation;
  protected onRequestDraw;
  protected id;

  constructor(onRequestDraw = () => null) {
    this.children = [];
    this.attributes = {};
    this.onRequestDraw = onRequestDraw;
    this.id = VirtualDomElement.idCounter++;
  }

  //helpful method for debugging
  drawBoundingBox(ctx, transform) {
    const bbox = this.getBoundingBox(transform);

    ctx.rect(bbox.getX(), bbox.getY(), bbox.getWidth(), bbox.getHeight());
    ctx.stroke();
  }

  // notify that changes need to be drawn (e.g. for animations)
  requestDraw() {
    this.onRequestDraw();
  }

  set(name, value) {
    this.setAttribute(name, value);
  }

  get(name) {
    return this.getAttribute(name);
  }

  setAttribute(name, value) {
    this.attributes[name] = value;
  }

  getId() {
    return this.id;
  }

  setAnimated(flag) {
    this._isAnimated = flag;
  }

  isAnimated() {
    return this._isAnimated;
  }

  isFadingIn() {
    return this._fadingIn;
  }

  isFadingOut() {
    return this._fadingOut;
  }

  setFadingIn(flag) {
    this._fadingIn = flag;
  }

  setFadingOut(flag) {
    this._fadingOut = flag;
  }

  fadeIn(duration = 1000) {
    if (!this.isFadingIn() && this.get('opacity') < 0.99) {
      if (this.isFadingOut() && this.fadeOutAnimation) {
        this.setFadingOut(false); //interrupt fading out animation
      }
      this.setFadingIn(true);
      this.fadeInAnimation = this.animateAttribute('opacity', 1, duration, () =>
        this.setFadingIn(false),
      );
    }
  }

  fadeOut(duration = 250) {
    if (!this.isFadingOut() && this.get('opacity') > 0.01) {
      if (this.isFadingIn() && this.fadeInAnimation) {
        this.setFadingIn(false); //interrupt fading in animation
      }
      this.setFadingOut(true);
      this.fadeOutAnimation = this.animateAttribute(
        'opacity',
        0,
        duration,
        () => this.setFadingOut(false),
      );
    }
  }

  animateAttribute(name, targetValue, duration = 1000, callback = () => null) {
    this.setAnimated(true);
    const currentValue = this.getAttribute(name);
    return d3
      .transition(this.id)
      .duration(duration)
      .tween(this.id, () => {
        const i = d3.interpolate(currentValue, targetValue);
        return t => {
          this.setAttribute(name, i(t));
          this.requestDraw();
        };
      })
      .on('end', callback);
  }

  getAttribute(name) {
    return this.attributes[name];
  }

  addChild(child) {
    this.children.push(child);
  }

  getClass() {
    return 'baseClass';
  }

  static fromDomElement(domElement) {
    const virtualDomelement = new VirtualDomElement();
    for (const attr of domElement.attributes) {
      virtualDomelement.setAttribute(attr.nodeName, attr.nodeValue);
    }

    return virtualDomelement;
  }

  getChildren() {
    return this.children;
  }

  // prevent subpixel calculation by reducing coordinates to non-floating points
  getFlooredCoordinates(coords) {
    return [Math.floor(coords[0]), Math.floor(coords[1])];
  }

  getTransformedWidthAndHeight(transform) {
    return [this.get('width') * transform.k, this.get('height') * transform.k];
  }

  getTransformedCoordinates(transform) {
    return transform.apply(this.get('x'), this.get('y'));
  }

  getBoundingBox(transform): BoundingBox {
    const [x, y] = transform.apply([
      this.getAttribute('x'),
      this.getAttribute('y'),
    ]);
    const width = this.get('width') * transform.k;
    const height = this.get('height') * transform.k;

    return new BoundingBox(x, x + width, y, y + height);
  }

  isRayTraced(mouseX, mouseY, transform) {
    //returns if true mouse is within bounds of element (ray tracing)
    const bbox = this.getBoundingBox(transform);

    return bbox.contains(mouseX, mouseY);
  }
}
